import React, { useState, useEffect, useMemo, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  Search, Filter, MapPin, Star, ChevronDown, Briefcase,
  Languages, Clock, Shield, CheckCircle, MessageSquare,
  Heart, User, Mail, X
} from "lucide-react";
import { useTranslation } from "react-i18next";
import MyProfile from "../../modules/professional/components/MyProfile";
import { Card } from "../../components/ui/card";
import { Badge } from "../../components/ui/badge";
import { Button } from "../../components/ui/Button";
import FavoriteButton from "../../components/ui/FavoriteButton";
import SharedHeader2 from "../../Headers/SharedHeader2";
import SharedFooter3 from "../../Footer/SharedFooter3";
import { useNavigate } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "../../redux/store";
import { getLocalProfessionals } from "../../redux/Actions/AdminAction";
import { getTypeCategories } from "../../redux/Actions/ClientActions";
import { getAllServiceAreas } from "../../redux/Actions/ServiceAreaAction";
import { selectLoaderValue } from "../../redux/Slices/loadingSlice";
import { useMultipleApiLoading } from "../../hooks/useMultipleApiLoading";
import { TypeEnum } from "../../modules/shared/model";
import SharedHeader5 from "../../Headers/SharedHeader5";
import RequestQuoteModal from "../../components/ui/RequestQuoteModal";
import { Offer } from "../../components/ui/RequestQuoteModal";
import SuccessNotification from "../../components/ui/SuccessNotification";
import ReviewModal from "../../components/ui/ReviewModal";
import { getTranslatedCategoryName } from "../../utils/categoryTranslation";
import { getDynamicCurrencySymbol } from "../../lib/currencyUtils";

// RealTimeFavoriteButton component moved outside to prevent recreation
interface RealTimeFavoriteButtonProps {
  professional: any;
  currentUserFavorite: any;
}

const RealTimeFavoriteButton = React.memo(({ professional, currentUserFavorite }: RealTimeFavoriteButtonProps) => {
  const [isFavorite, setIsFavorite] = useState(professional.favorite?.id ? true : false);
  
  // Listen for changes in currentUserFavorite to update the button state
  useEffect(() => {
    if (currentUserFavorite && currentUserFavorite.userIds) {
      const isFavorited = currentUserFavorite.userIds.some(user => user.id === professional.id.toString());
      setIsFavorite(isFavorited);
    } else if (currentUserFavorite === null) {
      // If no favorites exist, set to false
      setIsFavorite(false);
    }
  }, [currentUserFavorite, professional.id]);

  return (
    <FavoriteButton
      targetUserId={professional.id.toString()}
      size="sm"
      favoriteData={{
        id: professional.favorite?.id,
        isFavorite: isFavorite
      }}
    />
  );
});

RealTimeFavoriteButton.displayName = "RealTimeFavoriteButton";

const LocalProfessionalsPage = () => {
  const { t, i18n } = useTranslation();
  const currentLang = i18n.language.split("-")[0];
  const [searchQuery, setSearchQuery] = useState("");
  const [categorySearchQuery, setCategorySearchQuery] = useState("");
  const [showCategoryDropdown, setShowCategoryDropdown] = useState(false);
  const [showMobileFilters, setShowMobileFilters] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState("all");
  const [categoriesToShow, setCategoriesToShow] = useState(8);
  const [serviceAreasToShow, setServiceAreasToShow] = useState(8);
  const [serviceAreasPage, setServiceAreasPage] = useState(1);
  const [serviceAreas, setServiceAreas] = useState([]);
  const [priceRange, setPriceRange] = useState([0, 100]);
  const [rating, setRating] = useState(0);
  const [availability, setAvailability] = useState("all");
  const [showLocationDropdown, setShowLocationDropdown] = useState(false);
  const [selectedLocation, setSelectedLocation] = useState("");
  const [selectedCountryId, setSelectedCountryId] = useState("");
  const [selectedCategoryId, setSelectedCategoryId] = useState("");
  const [showProfileDialog, setShowProfileDialog] = useState(false);
  const [selectedProfessional, setSelectedProfessional] = useState(null);
  const [showEmergencyOnly, setShowEmergencyOnly] = useState(false);
  const [expandedCategories, setExpandedCategories] = useState({});
  const [visibleSubCounts, setVisibleSubCounts] = useState<Record<string, number>>({});
  const [showRequestQuoteModal, setShowRequestQuoteModal] = useState(false);
  const [isContact, setIsContact] = useState(false);
  const [selectedProfessionalForQuote, setSelectedProfessionalForQuote] = useState(null);
  const [showSuccessNotification, setShowSuccessNotification] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");
  const [editingOfferId, setEditingOfferId] = useState<string | undefined>(undefined);
  const [editingOffer, setEditingOffer] = useState<any>(null);
  const [showReviewModal, setShowReviewModal] = useState(false);
  const [selectedProfessionalForReview, setSelectedProfessionalForReview] = useState(null);

  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const isLoading = useAppSelector(selectLoaderValue);
  const { data: professionals, totalCount, hasNextPage, currentPage: storeCurrentPage, totalPages } = useAppSelector((state: any) => state.admin.localProfessionals);
  const { typeCategories } = useAppSelector((state: any) => state.client);
  const { serviceAreas: serviceAreasData } = useAppSelector((state: any) => state.serviceArea);
  const { user } = useAppSelector((state: any) => state.auth);
  const { currentUserFavorite } = useAppSelector((state: any) => state.favorites);

  // Define the API tasks that need to complete before showing content
  const apiTasks = ["localProfessionals", "typeCategories", "serviceAreas"];

  // Use the multiple API loading hook
  const {
    isAnyLoading: isInitialLoading,
    isAllCompleted,
    startTask,
    completeTask
  } = useMultipleApiLoading({
    taskIds: apiTasks,
    onAllCompleted: () => {
      // All initial APIs completed
    },
    resetOnMount: true
  });

  // Transform service areas data for UI
  const serviceAreasList = useMemo(() => {
    if (!serviceAreasData?.data || !Array.isArray(serviceAreasData.data)) {
      return serviceAreas; // Fallback to hardcoded list
    }

    return serviceAreasData.data
      .filter((area: any) => area && typeof area === "object") // Filter out null/undefined items
      .map((area: any) => {
        // Return the full area object to preserve id and other properties
        return {
          id: area.id || area.serviceAreaId || "",
          name: area.name || (area.cities && area.cities.length > 0 ? area.cities[0] : "Unknown Location"),
          displayName: area.name || (area.cities && area.cities.length > 0 ? area.cities[0] : "Unknown Location"),
          serviceAreaId: area.country?.id || area.id || ""
        };
      });
  }, [serviceAreasData, serviceAreas]);

  // Filter service areas based on search query
  const filteredServiceAreas = useMemo(() => {
    if (!searchQuery.trim()) {
      return serviceAreasList;
    }
    return serviceAreasList.filter(location =>
      location.displayName.toLowerCase().includes(searchQuery.toLowerCase())
    );
  }, [serviceAreasList, searchQuery]);

  // Get service areas to display
  const displayedServiceAreas = useMemo(() => {
    return filteredServiceAreas.slice(0, serviceAreasToShow);
  }, [filteredServiceAreas, serviceAreasToShow]);

  // Check if there are more service areas to show
  const hasMoreServiceAreas = serviceAreasData.hasNextPage || filteredServiceAreas.length > serviceAreasToShow;

  // Handle showing more service areas
  const handleShowMoreServiceAreas = async () => {
    if (serviceAreasData.hasNextPage) {
      // Fetch more from API
      const nextPage = serviceAreasPage + 1;
      try {
        await dispatch(getAllServiceAreas({
          page: nextPage,
          limit: 10,
          search: searchQuery.trim() || undefined,
          isShowLoader: false
        }));
        setServiceAreasPage(nextPage);
      } catch (error) {
        console.error("Error fetching more service areas:", error);
      }
    } else {
      // Show more from current data
      setServiceAreasToShow(prev => Math.min(prev + 8, filteredServiceAreas.length));
    }
  };

  useEffect(() => {
    // Only fetch professionals if user is available
    if (!user?.id) {
      completeTask("localProfessionals");
      return;
    }

    startTask("localProfessionals");
    dispatch(getLocalProfessionals({
      page: 1,
      limit: 10,
      jobUserId: user.id,
      categoryId: selectedCategoryId,
      serviceAreaId: selectedCountryId,
      minHourlyRate: priceRange[0] > 0 ? priceRange[0] : undefined,
      maxHourlyRate: priceRange[1] > 0 ? priceRange[1] : undefined,
      isEmergency: showEmergencyOnly ? true : undefined
    })).then(() => {
      completeTask("localProfessionals");
    }).catch(() => {
      completeTask("localProfessionals");
    });

  }, [selectedCategoryId, dispatch, user?.id]);


  // Handle showing fewer service areas
  const handleShowLessServiceAreas = () => {
    setServiceAreasToShow(8);
  };

  

  // Fetch categories from API
  useEffect(() => {
    const fetchCategories = async () => {
      try {
        startTask("typeCategories");
        // Fetch categories for both Home Service and Professional Service
        await Promise.all([
          dispatch(getTypeCategories({ id: [TypeEnum.Home_Service, TypeEnum.Professional_Service].toString() })),
        ]);
        completeTask("typeCategories");
      } catch (error) {
        console.error("Error fetching categories:", error);
        completeTask("typeCategories");
      }
    };
    fetchCategories();
  }, [dispatch]);

  

  // Search service areas when search query changes
  useEffect(() => {
    const searchServiceAreas = async () => {
      try {
        await dispatch(getAllServiceAreas({
          page: 1,
          limit: 10,
          search: searchQuery.trim() || undefined,
          isShowLoader: false
        }));
        setServiceAreasPage(1); // Reset to first page when searching
      } catch (error) {
        console.error("Error searching service areas:", error);
      }
    };

    // Debounce search to avoid too many API calls
    const timeoutId = setTimeout(() => {
      if (searchQuery.trim() !== "") {
        searchServiceAreas();
      } else {
        // If search is cleared, fetch initial data
        dispatch(getAllServiceAreas({
          page: 1,
          limit: 10,
          isShowLoader: false
        }));
        setServiceAreasPage(1);
      }
    }, 500);

    return () => clearTimeout(timeoutId);
  }, [searchQuery, dispatch]);



  // Transform categories data for UI with translations
  const categories = useMemo(() => {
    if (!typeCategories || !Array.isArray(typeCategories)) {
      return [{ name: t("localProfessionals.allCategories"), id: "", isAllOption: true }];
    }

    return [
      { name: t("localProfessionals.allCategories"), id: "", isAllOption: true },
      ...typeCategories.map((category: any) => ({
        name: getTranslatedCategoryName(category, currentLang) || category.name || "Unknown Category",
        originalName: category.name,
        id: category.id || "",
        translatedName: category.translatedName,
        type: category.type,
        isAllOption: false
      }))
    ];
  }, [typeCategories, currentLang, t]);

  // Filter categories based on search query
  const filteredCategories = useMemo(() => {
    if (!categorySearchQuery.trim()) {
      return categories;
    }
    return categories.filter(category =>
      category.name.toLowerCase().includes(categorySearchQuery.toLowerCase())
    );
  }, [categories, categorySearchQuery]);

  // Get categories to display (first N + "All Categories")
  const displayedCategories = useMemo(() => {
    return filteredCategories.slice(0, categoriesToShow);
  }, [filteredCategories, categoriesToShow]);

  // Check if there are more categories to show
  const hasMoreCategories = filteredCategories.length > categoriesToShow;

  // Handle showing more categories
  const handleShowMoreCategories = () => {
    setCategoriesToShow(prev => Math.min(prev + 8, filteredCategories.length));
  };

  // Handle showing fewer categories
  const handleShowLessCategories = () => {
    setCategoriesToShow(8);
  };

  // Handle search
  const handleSearch = () => {
    if (!user?.id) return;

    dispatch(getLocalProfessionals({
      page: 1,
      limit: 10,
      jobUserId: user.id,
      category: selectedCategory !== "all" ? selectedCategory : undefined,
      categoryId: selectedCategoryId,
      serviceAreaId: selectedCountryId,
      minHourlyRate: priceRange[0] > 0 ? priceRange[0] : undefined,
      maxHourlyRate: priceRange[1] > 0 ? priceRange[1] : undefined,
      isEmergency: showEmergencyOnly ? true : undefined
    }));
  };

  // Handle page change
  const handlePageChange = (page: number) => {
    if (!user?.id) return;

    dispatch(getLocalProfessionals({
      page,
      limit: 10,
      jobUserId: user.id,
      category: selectedCategory !== "all" ? selectedCategory : undefined,
      categoryId: selectedCategoryId,
      serviceAreaId: selectedCountryId,
      minHourlyRate: priceRange[0] > 0 ? priceRange[0] : undefined,
      maxHourlyRate: priceRange[1] > 0 ? priceRange[1] : undefined,
      isEmergency: showEmergencyOnly ? true : undefined
    }));
  };


  interface ProfessionalCardProps {
    professional: any;
  }

  const ProfessionalCard = React.memo(({ professional }: ProfessionalCardProps) => (
    <Card className="p-4 sm:p-6 hover:shadow-lg transition-shadow">
      <div className="flex flex-col sm:flex-row items-start gap-4">
        {/* Profile Image */}
        <div className="flex-shrink-0 w-full sm:w-auto flex justify-center sm:block">
          {professional.photo?.path ? (
            <div className="w-20 h-20 sm:w-16 sm:h-16 rounded-full overflow-hidden">
              <img
                src={professional.photo.path}
                alt={`${professional.name}'s profile`}
                className="w-full h-full object-cover"
                onError={(e) => {
                  // Fallback to default avatar if image fails to load
                  const target = e.target as HTMLImageElement;
                  target.style.display = "none";
                  const fallback = target.nextSibling as HTMLElement;
                  if (fallback) {
                    fallback.style.display = "flex";
                  }
                }}
              />
              <div className="w-full h-full bg-gradient-to-br from-blue-100 to-blue-200 flex items-center justify-center" style={{ display: "none" }}>
                <User className="w-10 h-10 sm:w-8 sm:h-8 text-blue-600" />
              </div>
            </div>
          ) : (
            <div className="w-20 h-20 sm:w-16 sm:h-16 rounded-full bg-gradient-to-br from-blue-100 to-blue-200 flex items-center justify-center">
              <User className="w-10 h-10 sm:w-8 sm:h-8 text-blue-600" />
            </div>
          )}
        </div>

        {/* Main Content */}
        <div className="flex-1 min-w-0 w-full">
          <div className="flex items-center justify-between mb-2">
            <h3 className="text-lg font-semibold text-gray-900 truncate">
              {professional.name}
            </h3>
            <div className="flex flex-end gap-2 items-center">
              <RealTimeFavoriteButton professional={professional} currentUserFavorite={currentUserFavorite} />
              <Button
                variant="outline"
                className="w-full flex items-center justify-center gap-2"
                onClick={() => {
                  setSelectedProfessional(professional);
                  setShowProfileDialog(true);
                }}
              >
                {t("localProfessionals.viewProfile")}
              </Button>
            </div>

          </div>

          <p className="text-sm text-gray-600 mb-2">{professional.title}</p>

          <div className="flex flex-wrap items-center gap-2 mb-3">
            <div className="flex items-center">
              <Star className="w-4 h-4 text-yellow-400 fill-current" />
              <span className="ml-1 text-sm font-medium">{professional.averageRating}</span>
            </div>
            <span className="text-gray-400 hidden sm:inline">•</span>
            <button
              onClick={() => {
                setSelectedProfessionalForReview(professional);
                setShowReviewModal(true);
              }}
              className="text-sm text-blue-600 hover:text-blue-700 hover:underline"
            >
              {professional.reviewsCount} {t("localProfessionals.reviews")}
            </button>
            <span className="text-gray-400 hidden sm:inline">•</span>
            <span className="text-sm text-gray-600">{professional.completedJobs} {t("localProfessionals.jobs")}</span>

            {/* Emergency Badge */}
            {professional.isEmergency && (
              <Badge variant={undefined} className="bg-red-100 text-red-700 border-red-200 flex items-center gap-1">
                <span className="w-2 h-2 bg-red-500 rounded-full animate-pulse"></span>
                {t("localProfessionals.emergency")}
              </Badge>
            )}
          </div>

          <p className="text-sm text-gray-600 mb-3 line-clamp-2 sm:line-clamp-none">
            {professional.description}
          </p>

          <div className="flex flex-wrap gap-2 mb-3">
            {professional.skills.map((skill, index) => (
              <Badge
                key={index}
                variant="outline"
                className="bg-blue-50 text-blue-700 border-blue-200 text-xs whitespace-nowrap"
              >
                {skill}
              </Badge>
            ))}
          </div>

          {/* Categories and Subcategories */}
          {professional.categories && professional.categories.length > 0 && (
            <div className="mb-3">
              <h4 className="text-sm font-medium text-gray-700 mb-2">{t("localProfessionals.categoriesSpecializations")}</h4>
              <div className="flex flex-wrap gap-2">
                {professional.categories.map((category, index) => {
                  const translatedName = getTranslatedCategoryName(category, currentLang);
                  const rawSubcategories = category.translatedSubCategories?.[currentLang] ?? category.subcategories ?? [];
                  const normalizedSubcategories = Array.isArray(rawSubcategories)
                    ? rawSubcategories
                    : typeof rawSubcategories === "string"
                      ? rawSubcategories
                        .split(",")
                        .map((item) => item.trim())
                        .filter(Boolean)
                      : [];
                  const hasSubcategories = normalizedSubcategories.length > 0;
                  return (
                  <div key={index} className="flex flex-col gap-1">
                    <Badge
                      variant="outline"
                      className={`text-xs ${category.isEmergency
                        ? "bg-red-50 text-red-700 border-red-200"
                        : "bg-green-50 text-green-700 border-green-200"
                        }`}
                    >
                      {translatedName}
                    </Badge>
                    {hasSubcategories && (
                      <div className="flex flex-col gap-1 ml-2">
                        <div className="flex flex-wrap gap-1">
                          {(normalizedSubcategories.slice(0, visibleSubCounts[`${professional.id}-${index}`] ?? 3)).map((subcategory, subIndex) => (
                            <Badge
                              key={subIndex}
                              variant="outline"
                              className="bg-gray-50 text-gray-600 border-gray-200 text-xs"
                            >
                              {subcategory}
                            </Badge>
                          ))}
                        </div>
                        {normalizedSubcategories.length > (visibleSubCounts[`${professional.id}-${index}`] ?? 3) && (
                          <button
                            onClick={() => setVisibleSubCounts(prev => ({
                              ...prev,
                              [`${professional.id}-${index}`]: (prev[`${professional.id}-${index}`] ?? 3) + 3
                            }))}
                            className="text-xs text-blue-600 hover:text-blue-700 hover:underline self-start"
                          >
                            {t("localProfessionals.showMore")}
                          </button>
                        )}
                        {(visibleSubCounts[`${professional.id}-${index}`] ?? 3) > 3 && (
                          <button
                            onClick={() => setVisibleSubCounts(prev => ({
                              ...prev,
                              [`${professional.id}-${index}`]: 3
                            }))}
                            className="text-xs text-blue-600 hover:text-blue-700 hover:underline self-start ml-2"
                          >
                            {t("localProfessionals.seeLess")}
                          </button>
                        )}
                      </div>
                    )}
                  </div>
                );})}
              </div>
            </div>
          )}

          <div className="flex flex-wrap items-center gap-x-4 gap-y-2 text-sm text-gray-600">
            <div className="flex items-center">
              <Clock className="w-4 h-4 mr-1" />
              {professional.responseTime}
            </div>
            <div className="flex items-center">
              <Languages className="w-4 h-4 mr-1" />
              {professional.languages.join(", ")}
            </div>
          </div>

          {/* Location Tags */}
          {professional.location && Array.isArray(professional.location) && professional.location.length > 0 && (
            <div className="mt-3">
              <h4 className="text-sm font-medium text-gray-700 mb-2">{t("localProfessionals.serviceLocations")}</h4>
              <div className="flex flex-wrap gap-2">
                {professional.location.map((location, index) => (
                  <div key={index + "xyz"} className="flex flex-col gap-1">
                    <Badge
                      variant="outline"
                      className="bg-blue-50 text-blue-700 border-blue-200 text-xs"
                    >
                      <MapPin className="w-3 h-3 mr-1" />
                      {location.province || t("localProfessionals.unknownProvince")}{location.cities && location.cities.length > 0 && (
                        <span>, {location.cities.join(", ")}</span>
                      )}
                    </Badge>
                    {/* {location.cities && location.cities.length > 0 && (
                      <div className="flex flex-wrap gap-1 ml-2">
                        {location.cities.map((city, cityIndex) => (
                          <Badge
                            key={cityIndex + "t"}
                            variant="outline"
                            className="bg-gray-50 text-gray-600 border-gray-200 text-xs"
                          >
                            {city}
                          </Badge>
                        ))}
                      </div>
                    )} */}
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className="mt-4 pt-4 border-t flex flex-col sm:flex-row sm:items-center gap-4 sm:justify-between">
            <div className="flex flex-wrap items-center gap-2">
              {professional.isEmergency && (
                <Badge variant={undefined} className="bg-red-100 text-red-700 border-red-200 flex items-center gap-1">
                  <span className="w-2 h-2 bg-red-500 rounded-full animate-pulse"></span>
                  {t("localProfessionals.emergencyService")}
                </Badge>
              )}
              {professional.verified && (
                <Badge variant={undefined} className="bg-green-50 text-green-700 flex items-center gap-1">
                  <CheckCircle className="w-3 h-3" />
                  {t("localProfessionals.verified")}
                </Badge>
              )}
              {professional.insured && (
                <Badge variant={undefined} className="bg-blue-50 text-blue-700 flex items-center gap-1">
                  <Shield className="w-3 h-3" />
                  {t("localProfessionals.insured")}
                </Badge>
              )}
            </div>
            <div className="flex items-center">
              <span className="text-lg font-semibold text-gray-900">
                {getDynamicCurrencySymbol()}{professional.hourlyRate}
              </span>
              <span className="text-sm text-gray-500">/hr</span>
            </div>
          </div>

          <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-2">
            <Button
              onClick={async () => {
                // const hasOffers = Array.isArray(professional.offers) && professional.offers.length > 0;
                // if (hasOffers) {
                //   const firstOffer = professional.offers[0];
                //   navigate(`/client/MessageAndVideoCall?professionalId=${professional.id}&professionalName=${encodeURIComponent(professional.name)}${firstOffer?.job?.id ? `&offerId=${firstOffer.id}` : ""}`);
                // } else {
                setSelectedProfessionalForQuote(professional);
                setIsContact(true);
                setEditingOfferId(undefined);
                setEditingOffer(null);
                setShowRequestQuoteModal(true);
                // }
              }}
              className="w-full bg-blue-600 text-white hover:bg-blue-700 flex items-center justify-center gap-2"
            >
              <MessageSquare className="w-4 h-4" />
              {t("localProfessionals.contact")}
            </Button>
            {/* <Button
              variant="outline"
              className="w-full flex items-center justify-center gap-2"
              onClick={() => {
                // const hasOffers = Array.isArray(professional.offers) && professional.offers.length > 0;
                // setSelectedProfessionalForQuote(professional);
                // if (hasOffers) {
                //   const firstOffer = professional.offers[0];
                //   setEditingOfferId(firstOffer?.id);
                //   setEditingOffer(firstOffer || null);
                // } else {
                //   setEditingOfferId(undefined);
                //   setEditingOffer(null);
                // }
                setEditingOfferId(undefined);
                setEditingOffer(null);
                setSelectedProfessionalForQuote(professional);
                setShowRequestQuoteModal(true);
              }}
            >
              <Mail className="w-4 h-4" />
              Request Quote
            </Button> */}
          </div>
        </div>
      </div>
    </Card>
  ));

  ProfessionalCard.displayName = "ProfessionalCard";

  interface FiltersPanelProps {
    isMobile?: boolean;
  }

  const FiltersPanel = React.memo(({ isMobile = false }: FiltersPanelProps) => (
    <div className="space-y-6 max-h-[calc(100vh-200px)] overflow-y-auto pr-2">
      {/* Categories */}
      <div>
        <h3 className="text-sm font-medium text-gray-900 mb-3">{t("localProfessionals.categories")}</h3>
        <div className="space-y-2">
          {displayedCategories.map((category, index) => {
            const categoryName = category.name;
            const categoryId = category.id;
            return (
              <label key={index + "g"} className="flex items-center">
                <input
                  type="radio"
                  name="category"
                  checked={selectedCategoryId === categoryId}
                  onChange={() => {
                    setSelectedCategory(categoryName);
                    setSelectedCategoryId(categoryId);
                  }}
                  className="w-4 h-4 text-blue-600 border-gray-300 focus:ring-blue-500"
                />
                <span className="ml-2 text-sm text-gray-600">{categoryName}</span>
              </label>
            );
          })}

          {/* Show More/See Less Buttons */}
          {hasMoreCategories && (
            <button
              onClick={handleShowMoreCategories}
              className="w-full py-2 text-sm text-blue-600 hover:text-blue-700 hover:underline mt-2"
            >
              {t("localProfessionals.showMore")} ({filteredCategories.length - categoriesToShow} more)
            </button>
          )}

          {categoriesToShow > 8 && (
            <button
              onClick={handleShowLessCategories}
              className="w-full py-2 text-sm text-gray-600 hover:text-gray-700 hover:underline mt-1"
            >
              {t("localProfessionals.seeLess")}
            </button>
          )}
        </div>
      </div>

      {/* Price Range */}
      <div>
        <h3 className="text-sm font-medium text-gray-900 mb-3">{t("localProfessionals.hourlyRate")}</h3>
        <div className="space-y-2">
          <input
            type="range"
            min="0"
            max="100"
            value={priceRange[1]}
            onChange={(e) => {
              const newMaxValue = parseInt(e.target.value);
              setPriceRange([priceRange[0], newMaxValue]);
            }}
            onMouseUp={(e) => {
              const newMaxValue = parseInt((e.target as HTMLInputElement).value);
              // Only call API when user stops dragging
              if (newMaxValue !== 100 && user?.id) {
                dispatch(getLocalProfessionals({
                  page: 1,
                  limit: 10,
                  jobUserId: user.id,
                  category: selectedCategory !== "all" ? selectedCategory : undefined,
                  categoryId: selectedCategoryId,
                  serviceAreaId: selectedCountryId,
                  minHourlyRate: priceRange[0] > 0 ? priceRange[0] : undefined,
                  maxHourlyRate: newMaxValue > 0 ? newMaxValue : undefined,
                  isEmergency: showEmergencyOnly ? true : undefined
                }));
              }
            }}
            onTouchEnd={(e) => {
              const newMaxValue = parseInt((e.target as HTMLInputElement).value);
              // Only call API when user stops touching (mobile)
              if (newMaxValue !== 100 && user?.id) {
                dispatch(getLocalProfessionals({
                  page: 1,
                  limit: 10,
                  jobUserId: user.id,
                  category: selectedCategory !== "all" ? selectedCategory : undefined,
                  categoryId: selectedCategoryId,
                  serviceAreaId: selectedCountryId,
                  minHourlyRate: priceRange[0] > 0 ? priceRange[0] : undefined,
                  maxHourlyRate: newMaxValue > 0 ? newMaxValue : undefined,
                  isEmergency: showEmergencyOnly ? true : undefined
                }));
              }
            }}
            className="w-full"
          />
          <div className="flex items-center justify-between text-sm text-gray-600">
            <span>{getDynamicCurrencySymbol()}{priceRange[0]}</span>
            <span>{getDynamicCurrencySymbol()}{priceRange[1]}</span>
          </div>
        </div>
      </div>

      {/* Rating Filter */}
      <div>
        <h3 className="text-sm font-medium text-gray-900 mb-3">{t("localProfessionals.rating")}</h3>
        <div className="space-y-2">
          {[4, 3, 2, 1].map((stars) => (
            <label key={stars} className="flex items-center">
              <input
                type="radio"
                name="rating"
                checked={rating === stars}
                onChange={() => {
                  setRating(stars);
                  // Trigger API call with rating filter
                  if (user?.id) {
                    dispatch(getLocalProfessionals({
                      page: 1,
                      limit: 10,
                      jobUserId: user.id,
                      category: selectedCategory !== "all" ? selectedCategory : undefined,
                      categoryId: selectedCategoryId,
                      serviceAreaId: selectedCountryId,
                      minHourlyRate: priceRange[0] > 0 ? priceRange[0] : undefined,
                      maxHourlyRate: priceRange[1] > 0 ? priceRange[1] : undefined,
                      isEmergency: showEmergencyOnly ? true : undefined
                    }));
                  }
                }}
                className="w-4 h-4 text-blue-600 border-gray-300 focus:ring-blue-500"
              />
              <span className="ml-2 flex items-center text-sm text-gray-600">
                {stars}+ <Star className="w-4 h-4 text-yellow-400 fill-current ml-1" />
              </span>
            </label>
          ))}
        </div>
      </div>

      {/* Availability */}
      <div>
        <h3 className="text-sm font-medium text-gray-900 mb-3">{t("localProfessionals.availability")}</h3>
        <div className="space-y-2">
          {["all", "today", "this_week"].map((period) => (
            <label key={period} className="flex items-center">
              <input
                type="radio"
                name="availability"
                checked={availability === period}
                onChange={() => {
                  setAvailability(period);
                  // Trigger API call with availability filter
                  if (user?.id) {
                    dispatch(getLocalProfessionals({
                      page: 1,
                      limit: 10,
                      jobUserId: user.id,
                      category: selectedCategory !== "all" ? selectedCategory : undefined,
                      categoryId: selectedCategoryId,
                      serviceAreaId: selectedCountryId,
                      minHourlyRate: priceRange[0] > 0 ? priceRange[0] : undefined,
                      maxHourlyRate: priceRange[1] > 0 ? priceRange[1] : undefined,
                      isEmergency: showEmergencyOnly ? true : undefined
                    }));
                  }
                }}
                className="w-4 h-4 text-blue-600 border-gray-300 focus:ring-blue-500"
              />
              <span className="ml-2 text-sm text-gray-600">
                {period === "all" ? t("localProfessionals.anyTime") :
                  period === "today" ? t("localProfessionals.today") :
                    t("localProfessionals.thisWeek")}
              </span>
            </label>
          ))}
        </div>
      </div>

      {/* Emergency Filter */}
      <div>
        <h3 className="text-sm font-medium text-gray-900 mb-3">{t("localProfessionals.serviceType")}</h3>
        <div className="space-y-2">
          <label className="flex items-center">
            <input
              type="checkbox"
              checked={showEmergencyOnly}
              onChange={(e) => {
                setShowEmergencyOnly(e.target.checked);
                // Trigger API call with emergency filter
                if (user?.id) {
                  dispatch(getLocalProfessionals({
                    page: 1,
                    limit: 10,
                    jobUserId: user.id,
                    category: selectedCategory !== "all" ? selectedCategory : undefined,
                    categoryId: selectedCategoryId,
                    serviceAreaId: selectedCountryId,
                    minHourlyRate: priceRange[0] > 0 ? priceRange[0] : undefined,
                    maxHourlyRate: priceRange[1] > 0 ? priceRange[1] : undefined,
                    isEmergency: e.target.checked ? true : undefined
                  }));
                }
              }}
              className="w-4 h-4 text-red-600 border-gray-300 rounded focus:ring-red-500"
            />
            <span className="ml-2 text-sm text-gray-600 flex items-center gap-2">
              <span className="w-2 h-2 bg-red-500 rounded-full"></span>
              {t("localProfessionals.emergencyServicesOnly")}
            </span>
          </label>
        </div>
      </div>

      {/* Verification Status */}
      <div>
        <h3 className="text-sm font-medium text-gray-900 mb-3">{t("localProfessionals.verification")}</h3>
        <div className="space-y-2">
          <label className="flex items-center">
            <input
              type="checkbox"
              className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
            />
            <span className="ml-2 text-sm text-gray-600">{t("localProfessionals.verifiedProfile")}</span>
          </label>
          <label className="flex items-center">
            <input
              type="checkbox"
              className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
            />
            <span className="ml-2 text-sm text-gray-600">{t("localProfessionals.insured")}</span>
          </label>
        </div>
      </div>

      {/* Reset Filters Button */}
      <button
        className="w-full py-2 text-sm text-blue-600 hover:text-blue-700 hover:underline"
        onClick={() => {
          setSelectedCategory("all");
          setSelectedCategoryId("");
          setCategorySearchQuery("");
          setCategoriesToShow(8);
          setServiceAreasToShow(8);
          setServiceAreasPage(1);
          setPriceRange([0, 100]);
          setRating(0);
          setAvailability("all");
          setShowEmergencyOnly(false);
          setExpandedCategories({});
          setVisibleSubCounts({});
          setSelectedLocation("");
          setSelectedCountryId("");

          // Trigger API call with reset filters
          if (user?.id) {
            dispatch(getLocalProfessionals({
              page: 1,
              limit: 10,
              jobUserId: user.id,
              category: undefined,
              categoryId: "",
              serviceAreaId: "",
              minHourlyRate: undefined,
              maxHourlyRate: undefined,
              isEmergency: undefined
            }));
          }

          if (isMobile) setShowMobileFilters(false);
        }}
      >
        {t("localProfessionals.resetFilters")}
      </button>
    </div>
  ));

  FiltersPanel.displayName = "FiltersPanel";

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      {/* <SharedHeader2 navigateBack={() => navigate(-1)} />  */}
      <SharedHeader5 />

      {/* Updated to handle back navigation */}

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 py-8" style={{ textAlign: "left" }}>
        {/* Enhanced Search Section */}
        <div className="mb-8 space-y-4">
          {/* Main Search Section */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Category Search */}
            <div className="relative">
              <Search className="absolute left-4 top-3.5 text-gray-400 w-5 h-5" />
              <input
                type="text"
                placeholder={t("localProfessionals.searchByCategory")}
                className="w-full pl-12 pr-10 py-3 rounded-lg border focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                value={categorySearchQuery}
                onChange={(e) => setCategorySearchQuery(e.target.value)}
                onClick={() => setShowCategoryDropdown(true)}
              />
              {categorySearchQuery && (
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    setCategorySearchQuery("");
                    setSelectedCategoryId("");
                  }}
                  className="absolute right-3 top-3.5 text-gray-400 hover:text-gray-600"
                >
                  <X className="w-4 h-4" />
                </button>
              )}

              {/* Location Dropdown */}
              <AnimatePresence>
                {showCategoryDropdown && (
                  <>
                    <motion.div
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      exit={{ opacity: 0 }}
                      className="fixed inset-0 z-10"
                      onClick={() => setShowCategoryDropdown(false)}
                    />
                    <motion.div
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: 10 }}
                      className="absolute z-20 w-full mt-1 bg-white border rounded-lg shadow-lg py-1"
                    >
                      <div className="max-h-60 overflow-y-auto">
                        {displayedCategories.map((category, index) => {
                          const categoryName = category.name;
                          const categoryId = category.id;
                          return (
                            <button
                              key={index}
                              className="w-full px-4 py-2 text-left hover:bg-gray-50 text-sm text-gray-700"
                              onClick={() => {
                                setSelectedCategory(categoryName);
                                setSelectedCategoryId(categoryId);
                                setShowCategoryDropdown(false);
                                setCategorySearchQuery(categoryName);

                                // Trigger API call with selected category
                                if (user?.id) {
                                  dispatch(getLocalProfessionals({
                                    page: 1,
                                    limit: 10,
                                    jobUserId: user.id,
                                    category: categoryName !== "all" ? categoryName : undefined,
                                    categoryId: categoryId,
                                    serviceAreaId: selectedCountryId,
                                    minHourlyRate: priceRange[0] > 0 ? priceRange[0] : undefined,
                                    maxHourlyRate: priceRange[1] > 0 ? priceRange[1] : undefined,
                                    isEmergency: showEmergencyOnly ? true : undefined
                                  }));
                                }
                              }}
                            >
                              <div className="flex items-center">
                                {categoryName}
                              </div>
                            </button>
                          );
                        })}

                        {/* Show More Button */}
                        {hasMoreCategories && (
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleShowMoreCategories();
                            }}
                            className="w-full px-4 py-2 text-left hover:bg-blue-50 text-sm text-blue-600 border-t border-gray-100"
                          >
                            Show More ({filteredCategories.length - categoriesToShow} more)
                          </button>
                        )}

                        {/* See Less Button */}
                        {categoriesToShow > 8 && (
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleShowLessCategories();
                            }}
                            className="w-full px-4 py-2 text-left hover:bg-gray-50 text-sm text-gray-600 border-t border-gray-100"
                          >
                            See Less
                          </button>
                        )}
                      </div>
                    </motion.div>
                  </>
                )}
              </AnimatePresence>
            </div>

            {/* Location Search */}
            <div className="relative">
              <MapPin className="absolute left-4 top-3.5 text-gray-400 w-5 h-5" />
              <input
                type="text"
                placeholder={t("localProfessionals.searchByLocation")}
                className="w-full pl-12 pr-10 py-3 rounded-lg border focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onClick={() => setShowLocationDropdown(true)}
              />
              {searchQuery && (
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    setSearchQuery("");
                    setSelectedCountryId("");
                  }}
                  className="absolute right-3 top-3.5 text-gray-400 hover:text-gray-600"
                >
                  <X className="w-4 h-4" />
                </button>
              )}

              {/* Location Dropdown */}
              <AnimatePresence>
                {showLocationDropdown && (
                  <>
                    <motion.div
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      exit={{ opacity: 0 }}
                      className="fixed inset-0 z-10"
                      onClick={() => setShowLocationDropdown(false)}
                    />
                    <motion.div
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: 10 }}
                      className="absolute z-20 w-full mt-1 bg-white border rounded-lg shadow-lg py-1"
                    >
                      <div className="max-h-60 overflow-y-auto">
                        {displayedServiceAreas.map((location, index) => (
                          <button
                            key={index}
                            className="w-full px-4 py-2 text-left hover:bg-gray-50 text-sm text-gray-700"
                            onClick={() => {
                              setSelectedLocation(location.displayName);
                              setSelectedCountryId(location.id); // Set country ID
                              setShowLocationDropdown(false);
                              setSearchQuery(location.displayName);

                              // Trigger API call with selected country ID
                              if (user?.id) {
                                dispatch(getLocalProfessionals({
                                  page: 1,
                                  limit: 10,
                                  jobUserId: user.id,
                                  category: selectedCategory !== "all" ? selectedCategory : undefined,
                                  serviceAreaId: location.id,
                                  minHourlyRate: priceRange[0] > 0 ? priceRange[0] : undefined,
                                  maxHourlyRate: priceRange[1] > 0 ? priceRange[1] : undefined,
                                  isEmergency: showEmergencyOnly ? true : undefined
                                }));
                              }
                            }}
                          >
                            <div className="flex items-center">
                              <MapPin className="w-4 h-4 mr-2 text-gray-400" />
                              {location.displayName}
                            </div>
                          </button>
                        ))}

                        {/* Show More Button for Service Areas */}
                        {hasMoreServiceAreas && (
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleShowMoreServiceAreas();
                            }}
                            className="w-full px-4 py-2 text-left hover:bg-blue-50 text-sm text-blue-600 border-t border-gray-100"
                          >
                            Show More {serviceAreasData.hasNextPage ? "" : `(${filteredServiceAreas.length - serviceAreasToShow} more)`}
                          </button>
                        )}

                        {/* See Less Button for Service Areas */}
                        {serviceAreasToShow > 8 && (
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleShowLessServiceAreas();
                            }}
                            className="w-full px-4 py-2 text-left hover:bg-gray-50 text-sm text-gray-600 border-t border-gray-100"
                          >
                            See Less
                          </button>
                        )}
                      </div>
                    </motion.div>
                  </>
                )}
              </AnimatePresence>
            </div>
          </div>

          {/* Search Button */}
          <div className="flex justify-center">
            <Button
              className="bg-blue-600 text-white px-8 py-3 rounded-lg hover:bg-blue-700 transition-colors w-full md:w-auto"
              onClick={handleSearch}
              disabled={isLoading || !user?.id}
            >
              {!user?.id ? t("localProfessionals.pleaseLogInToSearch") : isLoading ? t("localProfessionals.searching") : t("localProfessionals.searchProfessionals")}
            </Button>
          </div>

          {/* Active Filters Display */}
          {(selectedCategoryId || selectedLocation || selectedCountryId || priceRange[0] > 0 || priceRange[1] < 100) && (
            <div className="flex flex-wrap gap-2 pt-4">
              {selectedCategoryId && (
                <Badge variant={undefined} className="bg-blue-100 text-blue-800 flex items-center gap-1">
                  <span>{selectedCategory}</span>
                  <button
                    onClick={() => {
                      setSelectedCategory("all");
                      setSelectedCategoryId("");
                      // Trigger API call without category filter
                      if (user?.id) {
                        dispatch(getLocalProfessionals({
                          page: 1,
                          limit: 10,
                          jobUserId: user.id,
                          category: undefined,
                          categoryId: "",
                          serviceAreaId: selectedCountryId,
                          minHourlyRate: priceRange[0] > 0 ? priceRange[0] : undefined,
                          maxHourlyRate: priceRange[1] > 0 ? priceRange[1] : undefined,
                          isEmergency: showEmergencyOnly ? true : undefined
                        }));
                      }
                    }}
                    className="ml-1 hover:text-blue-600"
                  >
                    <X className="w-3 h-3" />
                  </button>
                </Badge>
              )}
              {selectedLocation && selectedCountryId && (
                <Badge variant={undefined} className="bg-blue-100 text-blue-800 flex items-center gap-1">
                  <MapPin className="w-3 h-3" />
                  <span>{selectedLocation}</span>
                  <button
                    onClick={() => {
                      setSelectedLocation("");
                      setSelectedCountryId("");
                      // Trigger API call without location filter
                      if (user?.id) {
                        dispatch(getLocalProfessionals({
                          page: 1,
                          limit: 10,
                          jobUserId: user.id,
                          category: selectedCategory !== "all" ? selectedCategory : undefined,
                          categoryId: selectedCategoryId,
                          serviceAreaId: "",
                          minHourlyRate: priceRange[0] > 0 ? priceRange[0] : undefined,
                          maxHourlyRate: priceRange[1] > 0 ? priceRange[1] : undefined,
                          isEmergency: showEmergencyOnly ? true : undefined
                        }));
                      }
                    }}
                    className="ml-1 hover:text-blue-600"
                  >
                    <X className="w-3 h-3" />
                  </button>
                </Badge>
              )}
              {(priceRange[0] > 0 || priceRange[1] < 100) && (
                <Badge variant={undefined} className="bg-green-100 text-green-800 flex items-center gap-1">
                  <span>{getDynamicCurrencySymbol()}{priceRange[0]} - {getDynamicCurrencySymbol()}{priceRange[1]}/hr</span>
                  <button
                    onClick={() => {
                      setPriceRange([0, 100]);
                      // Trigger API call without price range filter
                      if (user?.id) {
                        dispatch(getLocalProfessionals({
                          page: 1,
                          limit: 10,
                          jobUserId: user.id,
                          category: selectedCategory !== "all" ? selectedCategory : undefined,
                          categoryId: selectedCategoryId,
                          serviceAreaId: selectedCountryId,
                          minHourlyRate: undefined,
                          maxHourlyRate: undefined,
                          isEmergency: showEmergencyOnly ? true : undefined
                        }));
                      }
                    }}
                    className="ml-1 hover:text-green-600"
                  >
                    <X className="w-3 h-3" />
                  </button>
                </Badge>
              )}
            </div>
          )}
        </div>

        <div className="flex flex-col lg:flex-row gap-8">
          {/* Desktop Filters Sidebar */}
          <div className="hidden lg:block w-64 flex-shrink-0">
            <Card className="p-6 sticky top-24">
              <FiltersPanel />
            </Card>
          </div>

          {/* Mobile Filters Dialog */}
          <AnimatePresence>
            {showMobileFilters && (
              <>
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  className="fixed inset-0 bg-black bg-opacity-50 z-50 lg:hidden"
                  onClick={() => setShowMobileFilters(false)}
                />
                <motion.div
                  initial={{ x: "100%" }}
                  animate={{ x: 0 }}
                  exit={{ x: "100%" }}
                  transition={{ type: "spring", bounce: 0, duration: 0.4 }}
                  className="fixed right-0 top-0 h-full w-full max-w-xs bg-white z-50 lg:hidden overflow-y-auto"
                >
                  <div className="p-4">
                    <div className="flex items-center justify-between mb-6">
                      <h2 className="text-lg font-semibold">{t("localProfessionals.filters")}</h2>
                      <button
                        onClick={() => setShowMobileFilters(false)}
                        className="p-2 hover:bg-gray-100 rounded-lg"
                      >
                        <X className="w-5 h-5" />
                      </button>
                    </div>
                    <FiltersPanel isMobile={true} />
                  </div>
                </motion.div>
              </>
            )}
          </AnimatePresence>

          {/* Professionals List */}
          <div className="flex-1">
            {/* Results Header */}
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center gap-4">
                <button
                  onClick={() => setShowMobileFilters(true)}
                  className="lg:hidden p-2 hover:bg-gray-100 rounded-lg"
                >
                  <Filter className="w-5 h-5 text-gray-600" />
                </button>
                <h2 className="text-lg sm:text-xl font-semibold text-gray-900">
                  {isInitialLoading ? t("localProfessionals.loading") : t("localProfessionals.professionalsFound", { count: totalCount || 0 })}
                </h2>
              </div>
              <div className="flex items-center">
                <select
                  className="px-3 py-2 border rounded-lg text-sm text-gray-600 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  defaultValue="recommended"
                >
                  <option value="recommended">{t("localProfessionals.recommended")}</option>
                  <option value="rating">{t("localProfessionals.highestRating")}</option>
                  <option value="price_low">{t("localProfessionals.lowestPrice")}</option>
                  <option value="price_high">{t("localProfessionals.highestPrice")}</option>
                  <option value="jobs">{t("localProfessionals.mostJobs")}</option>
                </select>
              </div>
            </div>

            {/* Professionals Grid */}
            <div className="space-y-4 sm:space-y-6">
              {!user?.id ? (
                <div className="text-center py-8">
                  <p className="text-gray-600">{t("localProfessionals.pleaseLogInToView")}</p>
                </div>
              ) : isInitialLoading ? (
                <div className="text-center py-8">
                  <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                  <p className="mt-2 text-gray-600">{t("localProfessionals.loadingProfessionals")}</p>
                </div>
              ) : professionals && professionals.length > 0 ? (
                professionals.map((professional) => (
                  <ProfessionalCard key={professional.id} professional={professional} />
                ))
              ) : (
                <div className="text-center py-8">
                  <p className="text-gray-600">{t("localProfessionals.noProfessionalsFound")}</p>
                </div>
              )}
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="mt-8 flex justify-center gap-2">
                {/* Previous Page Button */}
                <button
                  onClick={() => handlePageChange(storeCurrentPage - 1)}
                  disabled={storeCurrentPage === 1}
                  className={`px-3 sm:px-4 py-2 rounded-lg text-sm ${storeCurrentPage === 1
                    ? "bg-gray-200 text-gray-400 cursor-not-allowed"
                    : "bg-white text-gray-600 hover:bg-gray-50"
                    }`}
                >
                  {t("localProfessionals.previous")}
                </button>

                {/* Page Numbers */}
                {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                  let pageNum;
                  if (totalPages <= 5) {
                    pageNum = i + 1;
                  } else if (storeCurrentPage <= 3) {
                    pageNum = i + 1;
                  } else if (storeCurrentPage >= totalPages - 2) {
                    pageNum = totalPages - 4 + i;
                  } else {
                    pageNum = storeCurrentPage - 2 + i;
                  }

                  return (
                    <button
                      key={pageNum}
                      onClick={() => handlePageChange(pageNum)}
                      className={`px-3 sm:px-4 py-2 rounded-lg text-sm ${pageNum === storeCurrentPage
                        ? "bg-blue-600 text-white"
                        : "bg-white text-gray-600 hover:bg-gray-50"
                        }`}
                    >
                      {pageNum}
                    </button>
                  );
                })}

                {/* Next Page Button */}
                <button
                  onClick={() => handlePageChange(storeCurrentPage + 1)}
                  disabled={storeCurrentPage === totalPages}
                  className={`px-3 sm:px-4 py-2 rounded-lg text-sm ${storeCurrentPage === totalPages
                    ? "bg-gray-200 text-gray-400 cursor-not-allowed"
                    : "bg-white text-gray-600 hover:bg-gray-50"
                    }`}
                >
                  {t("localProfessionals.next")}
                </button>
              </div>
            )}
          </div>
        </div>
      </main>
      <br /><br /><br /><br />

      {/* Profile Dialog */}
      <AnimatePresence>
        {showProfileDialog && (
          <>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 bg-black bg-opacity-50 z-50"
              onClick={() => setShowProfileDialog(false)}
            />
            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 20 }}
              className="fixed inset-4 z-50 overflow-hidden"
            >
              <div className="h-full bg-white rounded-lg shadow-xl flex flex-col">
                {/* Dialog Header */}
                <div className="flex items-center justify-between p-4 border-b">
                  <h2 className="text-lg font-semibold text-gray-900">
                    {t("localProfessionals.profileTitle", { name: selectedProfessional?.name })}
                  </h2>
                  <button
                    onClick={() => setShowProfileDialog(false)}
                    className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                  >
                    <X className="w-5 h-5 text-gray-500" />
                  </button>
                </div>

                {/* Dialog Content */}
                <div className="flex-1 overflow-y-auto p-4">
                  {selectedProfessional && (
                    <MyProfile
                      isAdminEdit={true}
                      isViewing={true}
                      adminUserId={selectedProfessional.id}
                      onUserUpdated={() => {
                        // Refresh the professionals list if needed
                        handleSearch();
                      }}
                    />
                  )}
                </div>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>

      {/* Success Notification */}
      <SuccessNotification
        isVisible={showSuccessNotification}
        message={successMessage}
        onClose={() => setShowSuccessNotification(false)}
      />

      {/* Request Quote Modal */}
      <RequestQuoteModal
        isOpen={showRequestQuoteModal}
        onClose={() => {
          setShowRequestQuoteModal(false);
          setSelectedProfessionalForQuote(null);
          setEditingOfferId(undefined);
          setEditingOffer(null);
        }}
        isLocal={true}
        professional={selectedProfessionalForQuote}
        offerId={editingOfferId}
        existingOffer={editingOffer}
        onOfferCreated={(offer: Offer) => {
          setSuccessMessage("The professional receives the quote and accepts it and must chat with the client (currently, the professional who tries to chat cannot find the client's chat and must wait for the client to chat first)");
          setShowSuccessNotification(true);
          if (isContact) {
            navigate(`/client/MessageAndVideoCall?professionalId=${selectedProfessionalForQuote.id}&professionalName=${encodeURIComponent(selectedProfessionalForQuote.name)}&offerId=${offer.id}`);
          }
        }}
        onOfferUpdated={(offer: Offer) => {
          setSuccessMessage("Quote request updated successfully!");
          setShowSuccessNotification(true);
        }}
      />

      {/* Review Modal */}
      <ReviewModal
        isOpen={showReviewModal}
        onClose={() => {
          setShowReviewModal(false);
          setSelectedProfessionalForReview(null);
        }}
        professional={selectedProfessionalForReview}
      />

      {/* Footer */}
      {!isLoading && <SharedFooter3 />}
    </div>
  );
};

export default LocalProfessionalsPage;